import { REGIONS, CITIES } from '@/utils/UK';
import { Grid, TextField, Autocomplete, FormControl, FormLabel } from '@mui/material';
import { Control, Controller, FieldErrors, UseFormGetValues } from 'react-hook-form';
import type { CompanyFormValues } from './types';
import PermissionContext from '@/Contexts/Permissions';
import { PermissionActionEnum, PermissionEntityEnum } from '@/graphql/generated/graphql';
import { useContext } from 'react';

interface CompanyFormProps {
  control: Control<CompanyFormValues, any, CompanyFormValues>;
  formErrors: FieldErrors<CompanyFormValues>;
  getValues: UseFormGetValues<CompanyFormValues>;
}

const CompanyAddress: React.FC<CompanyFormProps> = ({ control, formErrors, getValues }) => {
  const { canPerformAction } = useContext(PermissionContext);
  const { Manage } = PermissionActionEnum;
  const { CompanySettings } = PermissionEntityEnum;
  const hasPermission = canPerformAction(Manage, CompanySettings);

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Controller
          name="street"
          control={control}
          rules={{ required: 'Street Address is required' }}
          render={({ field }) => (
            <FormControl sx={{ flex: 1, width: '100%' }}>
              <FormLabel htmlFor="street" sx={{ fontSize: 12, mb: 0.5 }}>
                Street Address
              </FormLabel>
              <TextField
                {...field}
                id="steet"
                fullWidth
                required
                size="small"
                variant="outlined"
                error={!!formErrors.street}
                helperText={formErrors.street?.message}
                disabled={!hasPermission}
              />
            </FormControl>
          )}
        />
      </Grid>

      <Grid size={4}>
        <Controller
          name="state"
          control={control}
          rules={{ required: 'State / Province is required' }}
          render={({ field }) => (
            <FormControl sx={{ flex: 1, width: '100%' }}>
              <FormLabel htmlFor="state" sx={{ fontSize: 12, mb: 0.5 }}>
                State / Province
              </FormLabel>
              <Autocomplete
                options={REGIONS}
                onChange={(_, newValue) => {
                  field.onChange(newValue);
                }}
                value={field.value}
                disabled={!hasPermission}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...field}
                    id="state"
                    size="small"
                    placeholder="State / Province"
                    error={!!formErrors.state}
                    helperText={formErrors.state?.message}
                  />
                )}
              />
            </FormControl>
          )}
        />
      </Grid>
      <Grid size={4}>
        <Controller
          name="city"
          control={control}
          rules={{ required: 'City is required' }}
          render={({ field }) => {
            const stateValue = getValues('state');
            return (
              <FormControl sx={{ flex: 1, width: '100%' }}>
                <FormLabel htmlFor="city" sx={{ fontSize: 12, mb: 0.5 }}>
                  City
                </FormLabel>
                <Autocomplete
                  {...field}
                  options={stateValue ? (CITIES[stateValue as keyof typeof CITIES] ?? []) : []}
                  onChange={(_, newValue) => {
                    field.onChange(newValue);
                  }}
                  disabled={!hasPermission || !stateValue}
                  value={field.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="City"
                      error={!!formErrors.city}
                      helperText={formErrors.city?.message}
                    />
                  )}
                />
              </FormControl>
            );
          }}
        />
      </Grid>

      <Grid size={4}>
        <Controller
          name="country"
          control={control}
          rules={{ required: 'Country is required' }}
          render={({ field }) => (
            <FormControl sx={{ flex: 1, width: '100%' }}>
              <FormLabel htmlFor="company-country" sx={{ fontSize: 12, mb: 0.5 }}>
                Country
              </FormLabel>
              <TextField
                {...field}
                fullWidth
                required
                variant="outlined"
                disabled
                size="small"
                error={!!formErrors.country}
                helperText={formErrors.country?.message}
              />
            </FormControl>
          )}
        />
      </Grid>
    </Grid>
  );
};

export default CompanyAddress;
